import { MergeKey, MergeKeysService } from 'api-clients/monolith';
import React, { VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { ConfirmationModal } from 'components/ConfirmationModal';
import { addMessageAction } from 'containers/FlashMessage/actions';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import globalMessages from 'shared/global/messages';

import { messages } from './messages';

// ********************************************************************************
// == Type ========================================================================
type Props = {
  accountExternalId: string;
  mergeKey: MergeKey;
  onClose: () => void;
  onDeleted: () => void;
};

// == Constant ====================================================================
const DIALOG_TITLE_ID = 'delete-merge-key-dialog-title';

const deleteService = (
  accountId: string,
  mergeKeyId: MergeKey['external_id'],
) => MergeKeysService.deleteInternalApiMergeKeys(accountId, mergeKeyId);

// == Component ===================================================================
const DeleteMergeKeyDialog: VFC<Props> = ({
  accountExternalId,
  mergeKey,
  onClose,
  onDeleted,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  // -- Mutation ------------------------------------------------------------------
  const { mutation: deleteMergeKey, result } = useApiServiceMutation<
    void,
    typeof deleteService
  >(deleteService, {
    onSuccess: () => {
      dispatch(
        addMessageAction(intl.formatMessage(messages.deleteSuccess), 'success'),
      );
      onDeleted();
      onClose();
    },
    onError: () => {
      dispatch(
        addMessageAction(intl.formatMessage(messages.deleteError), 'error'),
      );
    },
  });

  // -- Handler -------------------------------------------------------------------
  const handleConfirm = () =>
    deleteMergeKey(accountExternalId, mergeKey.external_id);

  // -- UI ------------------------------------------------------------------------
  const title = intl.formatMessage(messages.title);
  return (
    <ConfirmationModal
      ariaLabelledBy={DIALOG_TITLE_ID}
      bodyContent={
        <FormattedMessage
          {...messages.description}
          values={{ key: mergeKey.key }}
        />
      }
      cancelButtonContent={<FormattedMessage {...globalMessages.cancel} />}
      confirmButtonContent={<FormattedMessage {...globalMessages.confirm} />}
      disabled={result.isLoading}
      isLoading={result.isLoading}
      negative
      onCancel={onClose}
      onConfirm={handleConfirm}
      title={title}
    />
  );
};

// == Export ======================================================================
export default DeleteMergeKeyDialog;
